import React, { cloneElement, useMemo, useLayoutEffect, useRef, useState, useCallback, useEffect } from 'react';
import cn from 'classnames';
require("./Tabs.css");
var styles = {
  "Root": "Tabs-module-Root_3Zx",
  "Container": "Tabs-module-Container_-gx",
  "SliderContainer": "Tabs-module-SliderContainer_1vm",
  "Slider": "Tabs-module-Slider_1MJ",
  "hasContainerCornersSquared": "Tabs-module-hasContainerCornersSquared_1Ww",
  "hasContainerCornersRounded": "Tabs-module-hasContainerCornersRounded_2FJ",
  "hasContainerBackgroundNeutral": "Tabs-module-hasContainerBackgroundNeutral_jxW",
  "hasContainerBackgroundLight": "Tabs-module-hasContainerBackgroundLight_2u5",
  "hasSliderBackgroundNeutral": "Tabs-module-hasSliderBackgroundNeutral_2gk",
  "hasSliderBackgroundLight": "Tabs-module-hasSliderBackgroundLight_2CG",
  "hasRoundedCorners": "Tabs-module-hasRoundedCorners_1XR",
  "hasSquaredCorners": "Tabs-module-hasSquaredCorners_3MU",
  "hasUnderline": "Tabs-module-hasUnderline_3Fa",
  "hasDisabledUnderline": "Tabs-module-hasDisabledUnderline_1jk"
};
export const TabsThemes = {
  neutral: 'neutral',
  light: 'light'
};
export const TabsVariants = {
  underlined: 'underlined',
  filled: 'filled',
  filledBg: 'filledBg'
};
export const Tabs = ({
  className = '',
  corners = 'squared',
  currentActiveTabIndex = 0,
  children: childrenProp = [],
  controlRef,
  innerRef,
  isLocked = false,
  lockIndexes = [],
  style,
  onTabClick,
  theme = 'neutral',
  variant = 'filledBg'
}) => {
  const children = useMemo(() => {
    return childrenProp.map((child, index) => {
      const isActive = index === currentActiveTabIndex;
      return /*#__PURE__*/cloneElement(child, {
        isActive
      });
    });
  }, [childrenProp, currentActiveTabIndex]);
  const [offset, setOffset] = useState({
    width: 0,
    left: 0,
    top: 0
  });
  const tabsContainerRef = useRef(null);
  const isFirstRender = useRef(true);
  const updateSliderSize = useCallback(() => {
    if (tabsContainerRef.current?.childNodes) {
      const activeTabElement = tabsContainerRef.current.childNodes[currentActiveTabIndex];
      queueMicrotask(() => {
        setOffset({
          width: activeTabElement.offsetWidth,
          left: activeTabElement.offsetLeft,
          top: activeTabElement.offsetTop
        });
      });
    }
  }, [currentActiveTabIndex]);
  useLayoutEffect(() => {
    if (isFirstRender.current) {
      updateSliderSize();
      isFirstRender.current = false;
    }
  }, [updateSliderSize]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateSliderSize);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateSliderSize);
      }
    };
  }, [updateSliderSize]);
  const handleTabClick = useCallback(e => {
    onTabClick && onTabClick(e);
    setOffset({
      width: e.target.offsetWidth,
      left: e.target.offsetLeft,
      top: e.target.offsetTop
    });
  }, [onTabClick]);
  return /*#__PURE__*/React.createElement("div", {
    className: cn(styles.Root, className, {
      [styles.hasContainerCornersSquared]: corners === 'squared',
      [styles.hasContainerCornersRounded]: corners === 'rounded',
      [styles.hasContainerBackgroundNeutral]: variant === 'filledBg' && theme === 'neutral',
      [styles.hasContainerBackgroundLight]: variant === 'filledBg' && theme === 'light'
    }),
    ref: innerRef || controlRef,
    style: style,
    role: "tablist"
  }, /*#__PURE__*/React.createElement("div", {
    ref: tabsContainerRef,
    className: styles.Container
  }, children.map((child, index) => {
    const isActive = index === currentActiveTabIndex;
    return /*#__PURE__*/cloneElement(child, {
      key: index,
      onClick: handleTabClick,
      isDisabled: isLocked || lockIndexes.includes(index),
      isActive,
      index,
      variant
    });
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.SliderContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: cn(styles.Slider, {
      [styles.hasSliderBackgroundNeutral]: variant !== 'underlined' && theme === 'neutral',
      [styles.hasSliderBackgroundLight]: variant !== 'underlined' && theme === 'light',
      [styles.hasRoundedCorners]: variant !== 'underlined' && corners === 'rounded',
      [styles.hasSquaredCorners]: variant !== 'underlined' && corners === 'squared',
      [styles.hasUnderline]: variant === 'underlined' && !isLocked,
      [styles.hasDisabledUnderline]: variant === 'underlined' && isLocked
    }),
    style: {
      width: `${offset.width}px`,
      transform: `translate3d(${offset.left}px, ${offset.top}px, 0)`
    }
  }))));
};
if (process.env.NODE_ENV !== 'production') {
  Tabs.displayName = 'Tabs';
}